import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTreeViewApiRef } from '@mui/x-tree-view';
import { TreeItemWithIcon } from 'components/treeselect/CustomTreeItem';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'components/Button';
import { Select } from 'components/select/Select';
import { SelectConfirmButton, SelectFooter } from 'components/select/SelectFooter';
import { TreeSelectContent } from './TreeSelectContent';

interface TreeSelectProps {
  label: string;
  items: TreeItemWithIcon[];
  selectedItems: string[];
  disabled?: boolean;
  setSelectedItems?: (itemIds: string[]) => void;
  className?: string;
  id?: string;
  showConfirmButton?: boolean;
  defaultExpandedItems?: string[];
}

export function TreeSelect({
  label,
  items,
  selectedItems,
  disabled = undefined,
  className = undefined,
  setSelectedItems = undefined,
  showConfirmButton = undefined,
  defaultExpandedItems = [],
  id = '',
}: TreeSelectProps) {
  const intl = useIntl();
  const [filter, setFilter] = useState('');
  const [expandedItems, setExpandedItems] = useState(defaultExpandedItems);
  const [orderedSelectedItems, setOrderedSelectedItems] = useState(selectedItems);
  const defaultExpandedItemsKey = defaultExpandedItems.join('|');

  useEffect(() => {
    setExpandedItems(defaultExpandedItems);
  }, [defaultExpandedItemsKey]);

  // Optimize selection lookups with Set for O(1) performance
  const selectedItemsSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const orderedSelectedItemsSet = useMemo(() => new Set(orderedSelectedItems), [orderedSelectedItems]);

  const apiRef = useTreeViewApiRef();
  const toggledItemRef = React.useRef<Record<string, boolean>>({});

  const addParentToChildren = (parent: TreeItemWithIcon): TreeItemWithIcon => {
    const children = parent.children ? parent.children.map(c => addParentToChildren(c)) : [];
    return { ...parent, children: children.map(child => ({ ...child, parent })) };
  };

  // Adding parents to items so we can deal with upward (de-)selections
  const itemsWithParents = useMemo(() => items.map(item => addParentToChildren(item)), [items]);

  // Calculate indeterminate states separately based on current selection
  const addIndeterminateStates = (parent: TreeItemWithIcon): TreeItemWithIcon => {
    const children = parent.children ? parent.children.map(c => addIndeterminateStates(c)) : [];
    const selectedChildren = children.filter(c => selectedItemsSet.has(c.id));
    const indeterminate = selectedChildren.length > 0 && selectedChildren.length < children.length;
    return { ...parent, indeterminate, children };
  };

  // Adding parents to items and mark indeterminate parents
  const parentisedItems = useMemo(
    () => itemsWithParents.map(item => addIndeterminateStates(item)),
    [itemsWithParents, selectedItemsSet]
  );

  const selectOptions = useMemo(() => {
    const flattenItems = (treeItems: TreeItemWithIcon[]): TreeItemWithIcon[] =>
      treeItems.flatMap(item => [item, ...(item.children ? flattenItems(item.children) : [])]);

    return flattenItems(items).map(item => ({ value: item.id, label: item.label }));
  }, [items]);

  const selectedLabels = useMemo(
    () =>
      selectedItems
        .map(selectedItem => selectOptions.find(option => option.value === selectedItem)?.label)
        .filter((label): label is string => label !== undefined),
    [selectOptions, selectedItems]
  );

  const selectedDisplayValue = selectedLabels.join(', ');
  const selectOrClearAll = () => {
    if (setSelectedItems) {
      if (selectedItems.length > 0) {
        setSelectedItems([]);
      } else {
        setSelectedItems(selectOptions.map(si => si.value));
      }
    }
  };

  // Hide filtered out items
  const markVisibility = (item: TreeItemWithIcon): TreeItemWithIcon => {
    const markedChildren = item.children
      ? item.children
          .map(child => markVisibility(child))
          .sort(
            (left, right) =>
              Number(orderedSelectedItemsSet.has(right.id)) - Number(orderedSelectedItemsSet.has(left.id))
          )
      : [];
    const visible =
      !filter ||
      item.label.toLowerCase().includes(filter.toLowerCase()) ||
      markedChildren.filter(child => child.visible).length > 0;
    return { ...item, children: markedChildren, visible };
  };

  const filteredItems = useMemo(
    () =>
      parentisedItems
        .map(item => markVisibility(item))
        .sort(
          (left, right) => Number(orderedSelectedItemsSet.has(right.id)) - Number(orderedSelectedItemsSet.has(left.id))
        ),
    [parentisedItems, filter, orderedSelectedItemsSet]
  );

  function getItemDescendantsIds(item: TreeItemWithIcon) {
    const ids: string[] = [];
    item.children?.forEach(child => {
      ids.push(child.id);
      ids.push(...getItemDescendantsIds(child));
    });

    return ids;
  }

  const handleItemSelectionToggle = (_: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
    toggledItemRef.current[itemId] = isSelected;
  };

  const handleItemClick = (event: React.MouseEvent, itemId: string) => {
    if (event.target instanceof HTMLElement && event.target.closest('input')) return;

    const item = apiRef.current?.getItem(itemId) as TreeItemWithIcon | undefined;
    if (!item) return;

    event.preventDefault();
    event.stopPropagation();

    if (!item.children?.length) {
      const isSelected = selectedItems.includes(itemId);

      apiRef.current?.selectItem({
        event,
        itemId,
        keepExistingSelection: true,
        shouldBeSelected: !isSelected,
      });
      return;
    }

    setExpandedItems(current =>
      current.includes(itemId) ? current.filter(id => id !== itemId) : [...current, itemId]
    );
  };

  const handleSelectedItemsChange = (_: React.SyntheticEvent, newSelectedItems: string[]) => {
    // Select / unselect the children of the toggled item
    const itemsToSelect: string[] = [];
    const itemsToUnSelect: string[] = [];
    Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
      const item = apiRef.current!.getItem(itemId) as TreeItemWithIcon;
      if (isSelected) {
        itemsToSelect.push(...getItemDescendantsIds(item));
      } else {
        getItemDescendantsIds(item).forEach(descendantId => {
          itemsToUnSelect.push(descendantId);
        });
      }

      if (item.parent) {
        if (!isSelected) {
          itemsToUnSelect.push(item.parent.id);
        } else {
          const siblings = (item.parent.children ?? []).filter(child => child.id !== item.id);
          const selectedSiblings = siblings.filter(curChild => selectedItemsSet.has(curChild.id));
          if (isSelected && siblings.length === selectedSiblings.length) {
            itemsToSelect.push(item.parent.id);
          }
        }
      }
    });

    const newSelectedItemsWithChildren = Array.from(
      new Set([...newSelectedItems, ...itemsToSelect].filter(itemId => !itemsToUnSelect.includes(itemId)))
    );

    if (setSelectedItems) setSelectedItems(newSelectedItemsWithChildren);
    toggledItemRef.current = {};
  };

  return (
    <Select
      label={label}
      value={selectedDisplayValue}
      selectedCount={selectedItems.length > 0 ? selectedItems.length : undefined}
      hasValue={selectedItems.length > 0}
      disabled={disabled}
      id={id}
      className={className}
      onOpen={() => setOrderedSelectedItems(selectedItems)}
      mode="tree"
      search={{
        value: filter,
        placeholder: intl.formatMessage({ id: 'select.search', defaultMessage: 'Search' }),
        onChange: setFilter,
      }}
      footer={
        <SelectFooter>
          <Button priority="secondary" onClick={selectOrClearAll} size="sm">
            {selectedItems.length > 0 ? (
              <FormattedMessage id="select.clear-all" defaultMessage="Clear All" />
            ) : (
              <FormattedMessage id="select.select-all" defaultMessage="Select All" />
            )}
          </Button>
          {showConfirmButton && (
            <SelectConfirmButton>
              <FormattedMessage id="select.confirm" defaultMessage="Confirm" />
            </SelectConfirmButton>
          )}
        </SelectFooter>
      }
    >
      <TreeSelectContent
        items={filteredItems}
        selectedItems={selectedItems}
        defaultExpandedItems={defaultExpandedItems}
        expandedItems={expandedItems}
        onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
        apiRef={apiRef}
        onSelectedItemsChange={handleSelectedItemsChange}
        onItemSelectionToggle={handleItemSelectionToggle}
        onItemClick={handleItemClick}
      />
    </Select>
  );
}
